import React, { useState, useMemo } from 'react';
import { Download, Weight, Settings2, Info, Anchor, Calendar, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from '../hooks/use-toast';
import { saveQuote } from '../lib/quotes';

const MARINE_CABLE_DATA = {
  weightFactor: 1.23,
  description: 'AGI 56 Conductor Marine Cable (Kevlar + H2O block + 100 mil jacket)'
};

const MarineCableCalculator = ({ activeYear, pricing }) => {
  const [interval, setInterval] = useState(3.0);
  const [numElectrodes, setNumElectrodes] = useState(28);
  const [leadIn, setLeadIn] = useState(30.0);
  const [tail, setTail] = useState(0.2);
  const [numConnectors, setNumConnectors] = useState(1);
  const [numAnchorPoints, setNumAnchorPoints] = useState(3);
  const [electrodeType, setElectrodeType] = useState('SS');

  // Derive prices from Settings props
  const marineCablePrice = pricing.marineCablePrice;
  const connectorPrice = pricing.marineConnectorPrice;
  const ssElectrodePrice = pricing.marineSSElectrodePrice;
  const graphiteElectrodePrice = pricing.marineGraphiteElectrodePrice;
  const fixedCost = pricing.marineFixedCost;
  const overheadMultiplier = pricing.marineOverheadMultiplier;
  const profitMultiplier = pricing.marineProfitMultiplier;

  const M_TO_FT = 3.28084;
  const LB_TO_KG = 0.453592;

  // Calculate pricing for Marine Cable using EXACT Excel formula
  // Stainless Steel: =ROUNDUP((+F40*O31+F39*M32+F35*M17+M29)*1.13,0)*1.3
  // Graphite: =ROUNDUP((+H40*O31+H39*M32+H35*M18+M29)*1.13,0)*1.3
  const results = useMemo(() => {
    // Calculate total length (F40 or H40 in Excel)
    // For marine: interval × (electrodes - 1) + leadIn + tail
    const totalLengthMeters = interval * (numElectrodes - 1) + leadIn + tail;
    const totalLengthFeet = totalLengthMeters * M_TO_FT;

    // Get electrode price (M17 for SS, M18 for Graphite)
    const electrodePrice = electrodeType === 'SS' ? ssElectrodePrice : graphiteElectrodePrice;

    // Apply EXACT Excel formula:
    // Part 1: F40 * O31 (total length × marine cable price per meter)
    const cableCost = totalLengthMeters * marineCablePrice;

    // Part 2: F39 * M32 (number of connectors × connector price)
    const connectorCost = numConnectors * connectorPrice;

    // Part 3: F35 * M17 or M18 (number of electrodes × electrode price)
    const electrodeCost = numElectrodes * electrodePrice;

    // Part 4: M29 (fixed cost - not multiplied by quantity)
    const fixedCostAmount = fixedCost;

    // Calculate subtotal before markup
    const subtotalBeforeMarkup = cableCost + connectorCost + electrodeCost + fixedCostAmount;

    // Apply overhead multiplier (1.13)
    const subtotalWithOverhead = subtotalBeforeMarkup * overheadMultiplier;

    // Apply ROUNDUP and profit multiplier (1.3)
    const totalPrice = Math.ceil(subtotalWithOverhead) * profitMultiplier;

    // Calculate weight
    // Marine cable is heavier: weightFactor is 1.23 lb/m
    const weightLbs = (totalLengthMeters * MARINE_CABLE_DATA.weightFactor) +
                      (numElectrodes * 1.03 / 16) +
                      (numConnectors * 4.5 / 16);
    const weightKg = weightLbs * LB_TO_KG;

    return {
      totalLengthMeters,
      totalLengthFeet,
      cableCost,
      connectorCost,
      electrodeCost,
      fixedCostAmount,
      subtotalBeforeMarkup,
      overheadAmount: subtotalWithOverhead - subtotalBeforeMarkup,
      subtotalWithOverhead,
      profitAmount: totalPrice - subtotalWithOverhead,
      totalPrice,
      weightLbs,
      weightKg
    };
  }, [interval, numElectrodes, leadIn, tail, numConnectors, electrodeType,
      marineCablePrice, connectorPrice, ssElectrodePrice, graphiteElectrodePrice,
      fixedCost, overheadMultiplier, profitMultiplier]);

  const exportJSON = () => {
    const exportData = {
      configuration: {
        cableType: 'AGI 56 Conductor Marine Cable',
        interval: interval + ' m',
        numElectrodes,
        leadIn: leadIn + ' m',
        tail: tail + ' m',
        totalLength: results.totalLengthMeters.toFixed(2) + ' m',
        electrodeType: electrodeType === 'SS' ? 'Stainless Steel' : 'Graphite',
        numConnectors,
        numAnchorPoints
      },
      pricing: {
        cableCost: results.cableCost.toFixed(2),
        connectorCost: results.connectorCost.toFixed(2),
        electrodeCost: results.electrodeCost.toFixed(2),
        fixedCost: results.fixedCostAmount.toFixed(2),
        subtotalBeforeMarkup: results.subtotalBeforeMarkup.toFixed(2),
        overheadAmount: results.overheadAmount.toFixed(2),
        subtotalWithOverhead: results.subtotalWithOverhead.toFixed(2),
        profitAmount: results.profitAmount.toFixed(2),
        totalPrice: results.totalPrice.toFixed(2),
        currency: 'USD'
      },
      weight: {
        pounds: results.weightLbs.toFixed(2),
        kilograms: results.weightKg.toFixed(2)
      },
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agi-marine-cable-quote-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Success', description: 'Marine cable quote exported as JSON' });
  };

  const exportCSV = () => {
    const csvContent = [
      'AGI Marine Cable Price Quote',
      `Generated: ${new Date().toLocaleString()}`,
      '',
      'Configuration',
      'Cable Type,AGI 56 Conductor Marine Cable',
      `Interval (spacing),${interval} m`,
      `Number of Electrodes,${numElectrodes}`,
      `Lead-in,${leadIn} m`,
      `Tail,${tail} m`,
      `Total Length,${results.totalLengthMeters.toFixed(2)} m (${results.totalLengthFeet.toFixed(2)} ft)`,
      `Connectors,${numConnectors}`,
      `Anchor Points,${numAnchorPoints}`,
      `Electrode Type,${electrodeType === 'SS' ? 'Stainless Steel' : 'Graphite'}`,
      '',
      'Pricing Breakdown',
      `Cable Cost,$${results.cableCost.toFixed(2)}`,
      `Connector Cost,$${results.connectorCost.toFixed(2)}`,
      `Electrode Cost,$${results.electrodeCost.toFixed(2)}`,
      `Fixed Cost (M29),$${results.fixedCostAmount.toFixed(2)}`,
      `Subtotal Before Markup,$${results.subtotalBeforeMarkup.toFixed(2)}`,
      `Overhead (${((overheadMultiplier - 1) * 100).toFixed(0)}%),$${results.overheadAmount.toFixed(2)}`,
      `Subtotal With Overhead,$${results.subtotalWithOverhead.toFixed(2)}`,
      `Profit Margin (${((profitMultiplier - 1) * 100).toFixed(0)}%),$${results.profitAmount.toFixed(2)}`,
      `TOTAL PRICE,$${results.totalPrice.toFixed(2)}`,
      '',
      'Weight',
      `Total Weight,${results.weightLbs.toFixed(2)} lbs (${results.weightKg.toFixed(2)} kg)`
    ].join('\\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agi-marine-cable-quote-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Success', description: 'Marine cable quote exported as CSV' });
  };

  const exportPDF = () => {
    const pdfHTML = `
<!DOCTYPE html>
<html>
<head>
  <title>AGI Marine Cable Price Quote</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
    .header { background: linear-gradient(135deg, #1e3a8a 0%, #06b6d4 100%); color: white; padding: 30px; border-radius: 10px; text-align: center; }
    .header h1 { margin: 0; font-size: 28px; }
    .section { margin: 30px 0; }
    .section h2 { color: #1e3a8a; border-bottom: 3px solid #06b6d4; padding-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
    th { background: #1e3a8a; color: white; }
    .total-row { background: #dbeafe; font-weight: bold; font-size: 18px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>AGI Marine Cable Price Quote</h1>
    <p>56 Conductor Marine Cable</p>
    <p>${new Date().toLocaleDateString()}</p>
  </div>

  <div class="section">
    <h2>Cable Configuration</h2>
    <table>
      <tr><td><strong>Cable Type:</strong></td><td>AGI 56 Conductor Marine (Kevlar + H2O block)</td></tr>
      <tr><td><strong>Interval (Spacing):</strong></td><td>${interval} meters</td></tr>
      <tr><td><strong>Number of Electrodes:</strong></td><td>${numElectrodes}</td></tr>
      <tr><td><strong>Lead-in Length:</strong></td><td>${leadIn} m</td></tr>
      <tr><td><strong>Tail Length:</strong></td><td>${tail} m</td></tr>
      <tr><td><strong>Total Cable Length:</strong></td><td>${results.totalLengthMeters.toFixed(2)} m</td></tr>
      <tr><td><strong>Number of Connectors:</strong></td><td>${numConnectors}</td></tr>
      <tr><td><strong>Number of Anchor Points:</strong></td><td>${numAnchorPoints}</td></tr>
      <tr><td><strong>Electrode Type:</strong></td><td>${electrodeType === 'SS' ? 'Stainless Steel' : 'Graphite'}</td></tr>
    </table>
  </div>

  <div class="section">
    <h2>Price Breakdown</h2>
    <table>
      <tbody>
        <tr><td>Cable Cost</td><td>$${results.cableCost.toFixed(2)}</td></tr>
        <tr><td>Connector Cost</td><td>$${results.connectorCost.toFixed(2)}</td></tr>
        <tr><td>Electrode Cost</td><td>$${results.electrodeCost.toFixed(2)}</td></tr>
        <tr><td>Fixed Cost (M29)</td><td>$${results.fixedCostAmount.toFixed(2)}</td></tr>
        <tr><td>Subtotal Before Markup</td><td>$${results.subtotalBeforeMarkup.toFixed(2)}</td></tr>
        <tr><td>Overhead (${((overheadMultiplier - 1) * 100).toFixed(0)}%)</td><td>$${results.overheadAmount.toFixed(2)}</td></tr>
        <tr><td>Subtotal With Overhead</td><td>$${results.subtotalWithOverhead.toFixed(2)}</td></tr>
        <tr><td>Profit Margin (${((profitMultiplier - 1) * 100).toFixed(0)}%)</td><td>$${results.profitAmount.toFixed(2)}</td></tr>
        <tr class="total-row"><td><strong>TOTAL PRICE</strong></td><td><strong>$${results.totalPrice.toFixed(2)}</strong></td></tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <h2>Weight</h2>
    <p>Total Weight: ${results.weightLbs.toFixed(2)} lbs (${results.weightKg.toFixed(2)} kg)</p>
  </div>
</body>
</html>`;

    const blob = new Blob([pdfHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agi-marine-cable-quote-${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Success', description: 'Marine cable quote exported as PDF (HTML format)' });
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-cyan-50 border-l-4 border-cyan-600 p-4 rounded">
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-cyan-600 mt-0.5 mr-3" />
            <div>
              <p className="text-sm text-cyan-900">
                <strong>Marine Cable Formula (from Excel):</strong>
              </p>
              <p className="text-xs font-mono text-cyan-800 mt-1">
                SS: ROUNDUP((Length×O31 + Connectors×M32 + Electrodes×M17 + M29) × {overheadMultiplier}, 0) × {profitMultiplier}
              </p>
              <p className="text-xs font-mono text-cyan-800">
                Graphite: ROUNDUP((Length×O31 + Connectors×M32 + Electrodes×M18 + M29) × {overheadMultiplier}, 0) × {profitMultiplier}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-lg ml-4 flex-shrink-0">
            <Calendar className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">{activeYear} Prices</span>
          </div>
        </div>
      </div>

      {/* Configuration Card */}
      <Card className="border-slate-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 border-b">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Anchor className="w-5 h-5 text-cyan-600" />
            Marine Cable Configuration
          </CardTitle>
          <CardDescription>AGI 56 Conductor Marine/Borehole Cable</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Interval */}
            <div className="space-y-2">
              <Label htmlFor="interval" className="text-slate-700 font-medium">
                Interval / Spacing (m)
              </Label>
              <Input
                id="interval"
                type="number"
                step="0.5"
                value={interval}
                onChange={(e) => setInterval(Number(e.target.value))}
                className="border-slate-300"
                min="0.1"
              />
            </div>

            {/* Number of Electrodes */}
            <div className="space-y-2">
              <Label htmlFor="numElectrodes" className="text-slate-700 font-medium">Number of Electrodes</Label>
              <Input
                id="numElectrodes"
                type="number"
                value={numElectrodes}
                onChange={(e) => setNumElectrodes(Number(e.target.value))}
                className="border-slate-300"
                min="1"
              />
            </div>

            {/* Lead-in */}
            <div className="space-y-2">
              <Label htmlFor="leadIn" className="text-slate-700 font-medium">Lead-in (m)</Label>
              <Input
                id="leadIn"
                type="number"
                step="0.5"
                value={leadIn}
                onChange={(e) => setLeadIn(Number(e.target.value))}
                className="border-slate-300"
                min="0"
              />
            </div>

            {/* Tail */}
            <div className="space-y-2">
              <Label htmlFor="tail" className="text-slate-700 font-medium">Tail (m)</Label>
              <Input
                id="tail"
                type="number"
                step="0.1"
                value={tail}
                onChange={(e) => setTail(Number(e.target.value))}
                className="border-slate-300"
                min="0"
              />
            </div>

            {/* Number of Connectors */}
            <div className="space-y-2">
              <Label htmlFor="numConnectors" className="text-slate-700 font-medium">Number of Connectors</Label>
              <Input
                id="numConnectors"
                type="number"
                value={numConnectors}
                onChange={(e) => setNumConnectors(Number(e.target.value))}
                className="border-slate-300"
                min="0"
              />
            </div>

            {/* Number of Anchor Points */}
            <div className="space-y-2">
              <Label htmlFor="numAnchorPoints" className="text-slate-700 font-medium">
                Number of Anchor Points
              </Label>
              <Input
                id="numAnchorPoints"
                type="number"
                value={numAnchorPoints}
                onChange={(e) => setNumAnchorPoints(Number(e.target.value))}
                className="border-slate-300"
                min="0"
              />
            </div>

            {/* Electrode Type */}
            <div className="space-y-2">
              <Label htmlFor="electrodeType" className="text-slate-700 font-medium">Electrode Type</Label>
              <Select value={electrodeType} onValueChange={setElectrodeType}>
                <SelectTrigger id="electrodeType" className="border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SS">Stainless Steel</SelectItem>
                  <SelectItem value="Graphite">Graphite</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Calculated Total Length and Price */}
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Total Cable Length (calculated):</div>
              <div className="text-2xl font-bold text-cyan-700">
                {results.totalLengthMeters.toFixed(2)} m ({results.totalLengthFeet.toFixed(2)} ft)
              </div>
              <div className="text-xs text-slate-500 mt-1">Interval × (Electrodes-1) + Lead-in + Tail</div>
            </div>

            <div className="p-4 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-lg border-2 border-cyan-300">
              <div className="text-sm text-cyan-700 mb-1 font-semibold">TOTAL PRICE (ROUNDUP Applied):</div>
              <div className="text-2xl font-bold text-cyan-700">
                ${results.totalPrice.toFixed(2)}
              </div>
              <div className="text-xs text-cyan-600 mt-1">Using {activeYear} pricing</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weight Results */}
      <Card className="border-blue-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-200">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Weight className="w-5 h-5 text-blue-600" />
            Weight Calculation
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Total Weight</div>
              <div className="text-3xl font-bold text-cyan-700">{results.weightLbs.toFixed(2)}</div>
              <div className="text-sm text-slate-500 mt-1">pounds (lbs)</div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Total Weight</div>
              <div className="text-3xl font-bold text-cyan-700">{results.weightKg.toFixed(2)}</div>
              <div className="text-sm text-slate-500 mt-1">kilograms (kg)</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Buttons */}
      <Card className="border-slate-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Download className="w-5 h-5 text-slate-700" />
            Export Quote
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={exportJSON}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export JSON
            </Button>
            <Button
              onClick={exportCSV}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={exportPDF}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button
              disabled
              title="Coming soon — requires database setup"
              className="bg-gradient-to-r from-emerald-600 to-green-600 text-white opacity-50 cursor-not-allowed"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Quote
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarineCableCalculator;
